import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera } from '@fortawesome/free-solid-svg-icons';
import Lightbox from 'react-images';
import AddressInputAutoComplete from '../../../../common/AddressInputAutoComplete';
import { modifyObject } from '../../../../../helpers/modifyObject';
import { editLocationPoint, createNewLocation, deleteLocationPoint } from '../../../../../actions/locations';
import { UIShowAlert } from '../../../../../actions/ui';
import Edit from '../../../../common/forms/buttons/Edit';
import Delete from '../../../../common/forms/buttons/Delete';
import Cancel from '../../../../common/forms/buttons/Cancel';
import Save from '../../../../common/forms/buttons/Save';
import Loader from '../../../../common/ui/Loader';
import UsersService from '../../../../../services/UsersService';
import Validator from '../../../../common/forms/Validator';
import ErrorMessage from '../../../../common/forms/ErrorMessage';

const emptyLocation = {
  location_name: '',
  address: '',
  state: '',
  city: '',
  zip: '',
  coordinates: '',
  job_location_type_id: 0,
  web_url: ''
};

class Location extends Validator {
  static propTypes = {
    company_id: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string
    ]).isRequired,
    isCreatingLocation: PropTypes.bool,
    isEmployersProfile: PropTypes.bool,
    location: PropTypes.object,
    locationsList: PropTypes.array.isRequired,
    editLocationPoint: PropTypes.func.isRequired,
    createNewLocation: PropTypes.func.isRequired,
    deleteLocationPoint: PropTypes.func.isRequired,
    closeNewLocationForm: PropTypes.func,
    getListOfLocations: PropTypes.func.isRequired
  };

  state = {
    isEditing: false,
    isLoading: false,
    ...emptyLocation,
    initialLocation: emptyLocation,
    lightboxIsOpen: false,
    currentImage: 0
  };

  validationRules = {
    location_name: { required: true },
    address: { required: true },
    state: { required: true },
    city: { required: true },
    zip: { required: true },
    coordinates: { required: true },
    job_location_type_id: { requiredIdField: true }
  };

  static getDerivedStateFromProps = (props, state) => {
    if (props.location && (props.location !== state.initialLocation)) {
      return {
        ...props.location,
        initialLocation: props.location
      };
    }
    return null;
  };

  editLocation = () => {
    this.setState({
      isEditing: true
    });
  };

  cancelEditing = () => {
    this.setState({
      isEditing: false,
      ...this.state.initialLocation
    });
    this._resetErrors();
  };

  deleteLocationPoint = id => {
    this.setState({
      isLoading: true
    });
    this.props.deleteLocationPoint(id).then(response => {
      this.setState({
        isLoading: false
      });
      if (response) {
        this.props.getListOfLocations();
      }
    });
  };

  removeLocation = () =>
    this.props.UIShowConfirm(
      'Are you sure you want to delete this location?',
      () => this.deleteLocationPoint(this.state.id)
    );

  handleChangeLocation = event => {
    const { name, value } = event.target;
    const intValue = parseInt(value, 10);
    this.setState({ [name]: name === 'job_location_type_id' ? intValue : value });
    if (name === 'web_url') {
      this.validationRules.web_url = value ? { isUrl: true } : null;
    }
  };

  handleChangeAddress = addressInfo => {
    const { address, city, state, zip, address_coordinates } = addressInfo;
    this.setState({
      address,
      city,
      state,
      zip,
      coordinates: address_coordinates
    });
  };

  closeLocationCreation = () => {
    this.setState({
      ...emptyLocation
    });
    this.props.closeNewLocationForm();
  };

  handleSubmit = event => {
    event.preventDefault();
    if (this.validate(this.validationRules)) {
      this.setState({
        isLoading: true
      });
      const { company_id } = this.props;
      const { location_name, address, state, city, zip, coordinates, job_location_type_id, web_url } = this.state;
      const locationData = modifyObject({
        location_name, address, state, city, zip, coordinates, job_location_type_id, web_url, company_id
      });
      const submitMethod = this.state.isEditing ? this.submitEditing : this.createLocation;
      submitMethod(locationData);
    }
  };

  submitEditing = locationData => {
    const { editLocationPoint, getListOfLocations } = this.props;
    editLocationPoint(this.state.id, locationData).then(response => {
      this.setState({
        isLoading: false,
        isEditing: false
      });
      if (response) {
        getListOfLocations();
      } else {
        this.setState({
          ...this.state.initialLocation
        });
      }
    });
  };

  createLocation = locationData => {
    const { getListOfLocations, createNewLocation } = this.props;
    createNewLocation(locationData).then(data => {
      if (data) {
        this.closeLocationCreation();
        getListOfLocations();
      }
    });
  };

  openLightbox = () => {
    this.setState({
      lightboxIsOpen: true
    });
  };

  closeLightbox = () => {
    this.setState({
      lightboxIsOpen: false,
      currentImage: 0
    });
  };

  goToPreviousImage = () => {
    this.setState({
      currentImage: this.state.currentImage - 1
    });
  };

  goToNextImage = () => {
    this.setState({
      currentImage: this.state.currentImage + 1
    });
  };

  handleClickImage = () => {
    const { currentImage, location } = this.state;
    if (currentImage === location.photos.length - 1) return;
    this.goToNextImage();
  };

  render () {
    const { locationsList, isCreatingLocation, isEmployersProfile } = this.props;
    const { isLoading, isEditing, lightboxIsOpen, currentImage, media, location_name, address, state, city, zip,
      job_location_type_id, web_url } = this.state;
    const addressInfo = {
      address,
      state,
      city,
      zip
    };
    let selectedLocationTypeLabel = '';
    const selectedLocationType = locationsList.find(listItem => listItem.id === job_location_type_id);
    if (selectedLocationType) {
      selectedLocationTypeLabel = selectedLocationType.label_en;
    }
    const isEditingMode = isEditing || isCreatingLocation;
    let images = [];
    if (media && media.length) {
      images = media.map(photoData => ({ src: photoData.file_url }));
    }
    const errors = this.validationErrors;
    const addressErrorMessage = (errors.address || errors.state || errors.city || errors.zip) ? 'Not valid' : '';

    return (
      <div className={`mb-3 col-12 ${isEmployersProfile ? 'col-md-6 col-xl-4' : 'col-lg-6'}`}>
        <form className='item-wrapper location-list-item d-flex' onSubmit={this.handleSubmit}>
          { isLoading ? (
            <div className='loader-wrapper'><Loader /></div>
          ) : (
            <Fragment>
              { !isCreatingLocation && UsersService.isAdmin() && (
                <React.Fragment>
                  {media && media.length ? (
                    <div
                      className='location-list-img'
                      style={{ backgroundImage: `url('${media[0].file_url}')` }}
                      onClick={this.openLightbox}
                    >
                      <span className='images-counter'>{media.length}</span>
                      <Lightbox
                        images={images}
                        currentImage={currentImage}
                        isOpen={lightboxIsOpen}
                        onClose={this.closeLightbox}
                        onClickImage={this.handleClickImage}
                        onClickNext={this.goToNextImage}
                        onClickPrev={this.goToPreviousImage}
                      />
                    </div>
                  ) : (
                    <div
                      className='location-list-img'
                    >
                      <div className='camera-icon'>
                        <FontAwesomeIcon icon={faCamera} size='lg' />
                      </div>
                    </div>
                  )}
                </React.Fragment>
              )}
              <div className='location-details-wrapper'>
                <div className='location-header-wrapper'>
                  <div className='d-flex justify-content-between align-items-center'>
                    { isEditingMode ? (
                      <div className='location-name-input-wrapper'>
                        <input
                          className='location-name-input input'
                          type='text'
                          name='location_name'
                          value={location_name || ''}
                          placeholder='Location Name'
                          onChange={this.handleChangeLocation}
                        />
                      </div>
                    ) : (
                      <h4>{location_name}</h4>
                    )}
                    <div className='ml-1'>
                      { isCreatingLocation ? (
                        <Cancel onClick={this.closeLocationCreation} />
                      ) : isEditingMode ? (
                        <div className='d-flex'>
                          <Cancel onClick={this.cancelEditing} />
                          <Save />
                        </div>
                      ) : (
                        <div className='flex-wrapper'>
                          <Edit onClick={this.editLocation} />
                          <Delete onClick={this.removeLocation} />
                        </div>
                      )}
                    </div>
                  </div>
                  <ErrorMessage errorText={errors.location_name} />
                </div>
                <AddressInputAutoComplete
                  noLabel
                  isDisabled={!isEditingMode}
                  addressInfo={addressInfo}
                  handleAddressChange={this.handleChangeAddress}
                  errorMessage={addressErrorMessage}
                />
                {isEditingMode ? (
                  <div className='address-item'>
                    <select
                      className='input'
                      value={job_location_type_id || 0}
                      name='job_location_type_id'
                      onChange={this.handleChangeLocation}
                    >
                      <option disabled value='0'>Select location</option>
                      { locationsList && locationsList.map(location =>
                        <option value={location.id} key={location.id}>
                          {location.label_en}
                        </option>
                      )}
                    </select>
                    <ErrorMessage errorText={errors.job_location_type_id} />
                  </div>
                ) : (
                  <div className='address-item'>Location Type: {selectedLocationTypeLabel}</div>
                )}
                { isEditingMode ? (
                  <Fragment>
                    <input
                      className='web-site-input input'
                      type='text'
                      name='web_url'
                      value={web_url || ''}
                      placeholder='Web Site (optional)'
                      onChange={this.handleChangeLocation}
                    />
                    <ErrorMessage errorText={errors.web_url} />
                  </Fragment>
                ) : (
                  <Fragment>
                    { web_url && (
                      <div>Web Site: <a className='link-primary' target='_blank' href={`//${web_url}`}>{web_url}</a></div>
                    )}
                  </Fragment>
                )}
                { isCreatingLocation && (
                  <button type='submit' className='mt-3 w-100 button button-primary'>Add Location</button>
                )}
              </div>
            </Fragment>
          )}
        </form>
      </div>
    );
  }
}

const mapDispatchToProps = {
  editLocationPoint,
  createNewLocation,
  deleteLocationPoint,
  UIShowAlert
};

export default connect(null, mapDispatchToProps)(Location);



// WEBPACK FOOTER //
// ./src/components/app/companyDetails/Tabs/Locations/Location.js